import { TextTruncate } from "components";
import { Tooltip } from "components/common/tooltip";
import { RouteNames } from "enums";
import {
  isUserDeleted,
  formatUserName,
  type PerformanceFilter,
  convertInteractionDisplayName,
  canUseChildAccount,
  isUserFromCurrentAccount,
  getLinkNavigationProps,
} from "features/Library/Common/utils/performanceUtils";
import {
  GenericPerformanceListWithServerPagination,
  type ExportAvailability,
  type FetchPageFn,
} from "features/Reporting/Content/shared/GenericPerformanceListWithServerPagination";
import { type PeopleActivityDetails } from "features/Reporting/types/people";
import { type ColumnOption } from "interfaces/columnOptions";
import { useCallback, useMemo } from "react";
import moment from "moment";
import { MoboLink } from "components/MoboLink/MoboLink";
import { useReportingFormatWithTimeZone } from "hooks/useReportingFormatWithTimeZone";
import { type UserAssetTypes } from "features/People/Users/Performance/types";
import { type PaginationParams } from "features/Library/Common/models";
import {
  buildPeopleActivityDrilldownQueryKey,
  fetchPeopleActivityDetailsPage,
} from "features/Reporting/People/queries";
import { bindAction } from "interfaces";
import { connect, type ConnectedProps } from "react-redux";
import type { AppDispatch } from "features/Application/globaltypes/redux";
import { setHasDataToExport } from "features/Reporting/state/export/exportSlice";

type ContentRouteResolver = {
  test: (selectedType: string) => boolean;
  to: (contentId: number) => string;
};

const contentRouteResolvers: ContentRouteResolver[] = [
  {
    test: (selectedType) => selectedType === "Flow Outreach" || /email/i.test(selectedType),
    to: (contentId) => `/${RouteNames.contentCommunications}/${RouteNames.emails}/${contentId}`,
  },
  {
    test: (selectedType) => /embedded\s+videos?/i.test(selectedType),
    to: (contentId) => `/${RouteNames.contentEmbeddedVideos}/${contentId}`,
  },
  { test: (selectedType) => /flow/i.test(selectedType), to: (contentId) => `/${RouteNames.contentFlows}/${contentId}` },
  {
    test: (selectedType) => /assessment/i.test(selectedType),
    to: (contentId) => `/${RouteNames.contentAssessments}/${contentId}`,
  },
  {
    test: (selectedType) => /video/i.test(selectedType),
    to: (contentId) => `/${RouteNames.contentVideos}/${contentId}`,
  },
  { test: (selectedType) => /pdf/i.test(selectedType), to: (contentId) => `/${RouteNames.contentPdfs}/${contentId}` },
  {
    test: (selectedType) => /survey/i.test(selectedType),
    to: (contentId) => `/${RouteNames.contentSurveys}/${contentId}`,
  },
  {
    test: (selectedType) => /event/i.test(selectedType),
    to: (contentId) => `/${RouteNames.contentEvents}/${contentId}`,
  },
  {
    test: (selectedType) => /messages?/i.test(selectedType),
    to: (contentId) => `/${RouteNames.contentCommunications}/${RouteNames.messages}/${contentId}`,
  },
  {
    test: (selectedType) => /links?/i.test(selectedType),
    to: (contentId) => `/${RouteNames.contentLinks}/${contentId}`,
  },
];

const resolveContentRoute = (selectedType: string, contentId: number) => {
  for (const resolver of contentRouteResolvers) {
    if (resolver.test(selectedType)) {
      return resolver.to(contentId);
    }
  }
  return null;
};

export type Props = {
  dateFilter: PerformanceFilter;
  selectedType: UserAssetTypes;
  selectedCategory: string;
  accountId: number;
  childAccountId?: number;
  hasCreateAccountsAddOn?: boolean;
  disableLinks?: boolean;
} & PropsFromRedux;

const PeopleActivityDrilldown = ({
  dateFilter,
  selectedType,
  selectedCategory,
  accountId,
  childAccountId,
  hasCreateAccountsAddOn,
  setIsExportEnabled,
  disableLinks,
}: Props) => {
  const formatDateTime = useReportingFormatWithTimeZone();
  const childAccountEnabled = canUseChildAccount(childAccountId, hasCreateAccountsAddOn);

  const updateExportDataState = useCallback(
    ({ hasDataToExport }: ExportAvailability) => {
      setIsExportEnabled({
        hasDataToExport,
        tooltipContent: "No data available for export. There is no activity data.",
      });
    },
    [setIsExportEnabled],
  );

  const filters = useMemo(
    () => ({ dateFilter, selectedType, selectedCategory }),
    [dateFilter, selectedType, selectedCategory],
  );

  const fetchPage = useCallback<FetchPageFn<PeopleActivityDetails>>(
    (params: PaginationParams, signal?: AbortSignal) => fetchPeopleActivityDetailsPage(filters, params, signal),
    [filters],
  );

  const queryKey = useMemo(() => buildPeopleActivityDrilldownQueryKey(filters), [filters]);

  type ActivityColumnOption = ColumnOption<PeopleActivityDetails> & { sortKey?: string };

  const columnOptions: ActivityColumnOption[] = useMemo(() => {
    return [
      {
        name: "TITLE",
        width: 8,
        isSortable: true,
        sortKey: "ContentTitle",
        render(item) {
          const isUserFromCurrent = isUserFromCurrentAccount(
            item.AccountId,
            accountId,
            childAccountId,
            hasCreateAccountsAddOn,
          );
          const { accountId: linkAccountId, target: linkTarget } = getLinkNavigationProps({
            childAccountEnabled,
            isUserFromCurrentAccount: isUserFromCurrent,
            childAccountId,
            fallbackAccountId: item.AccountId,
          });

          const shouldShowLink = (childAccountEnabled || !childAccountId) && !disableLinks;

          const route = resolveContentRoute(selectedType, item.ContentId);
          const contentTitle = item.ContentTitle;
          const shouldLink = Boolean(route) && shouldShowLink;

          return (
            <Tooltip
              target={
                <TextTruncate>
                  {shouldLink ? (
                    <MoboLink target={linkTarget} accountId={linkAccountId} to={route ?? ""}>
                      {contentTitle}
                    </MoboLink>
                  ) : (
                    contentTitle
                  )}
                </TextTruncate>
              }
              content={`${contentTitle}`}
            />
          );
        },
      },
      {
        name: "USER",
        width: 8,
        isSortable: true,
        sortKey: "LastName",
        render(item) {
          const isUserFromCurrent = isUserFromCurrentAccount(
            item.AccountId,
            accountId,
            childAccountId,
            hasCreateAccountsAddOn,
          );
          const { accountId: linkAccountId, target: linkTarget } = getLinkNavigationProps({
            childAccountEnabled,
            isUserFromCurrentAccount: isUserFromCurrent,
            childAccountId,
            fallbackAccountId: item.AccountId,
          });

          const shouldShowLink =
            !isUserDeleted(item) && item.UserId !== null && (childAccountEnabled || !childAccountId) && !disableLinks;
          const formattedName = formatUserName(item.FirstName, item.LastName);
          return (
            <Tooltip
              target={
                <TextTruncate className={shouldShowLink ? "userLink" : ""}>
                  {shouldShowLink ? (
                    <MoboLink
                      target={linkTarget}
                      accountId={linkAccountId}
                      to={`/${RouteNames.peopleUsers}/${item.UserId}`}
                    >
                      {formattedName}
                    </MoboLink>
                  ) : (
                    formattedName
                  )}
                </TextTruncate>
              }
              content={formattedName}
            />
          );
        },
      },
      {
        name: "Department",
        width: 5,
        isSortable: true,
        sortKey: "Department",
        render(item) {
          const department = item.Department || "-";
          return <Tooltip target={<TextTruncate>{department}</TextTruncate>} content={department} />;
        },
      },
      {
        name: "JOB TITLE",
        width: 5,
        isSortable: true,
        sortKey: "JobTitle",
        render(item) {
          const title = item.JobTitle || "-";
          return <Tooltip target={<TextTruncate>{title}</TextTruncate>} content={title} />;
        },
      },
      {
        name: "DATE",
        width: 5,
        isSortable: true,
        sortKey: "EventDate",
        render(item) {
          const date = moment.utc(`${item.EventDate} ${item.EventTime}`);
          const formattedDateTime = formatDateTime(date);
          const datePart = formattedDateTime.split(" ")[0];
          return datePart;
        },
      },
      {
        name: "TIME",
        width: 5,
        isSortable: true,
        sortKey: "EventTime",
        render(item) {
          const date = moment.utc(`${item.EventDate} ${item.EventTime}`);
          const formattedDateTime = formatDateTime(date);
          const timePart = formattedDateTime.split(" ").slice(1).join(" ");
          return timePart;
        },
      },
    ];
  }, [
    selectedType,
    formatDateTime,
    childAccountEnabled,
    childAccountId,
    hasCreateAccountsAddOn,
    accountId,
    disableLinks,
  ]);

  return (
    <div className="people-activity-drilldown">
      <GenericPerformanceListWithServerPagination
        title={convertInteractionDisplayName(selectedType)}
        columns={columnOptions}
        filter={dateFilter}
        fetchPage={fetchPage}
        queryKeyBase={queryKey}
        hasDataForExport={updateExportDataState}
      />
    </div>
  );
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    setIsExportEnabled: bindAction(setHasDataToExport, dispatch),
  };
};

const connector = connect(null, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedPeopleActivityDrilldown = connector(PeopleActivityDrilldown);
export default ConnectedPeopleActivityDrilldown;
